/*
  Lumio promotions page. One script for every lumio-promo-* section.
  Installed by Console; the next install overwrites edits to this file.

  WHY THE STATE IS DECIDED IN THE BROWSER
  Shopify's page cache can serve a render minutes old, so a Liquid clock check
  would bake yesterday's answer into today's page. Every state is rendered
  hidden and this script picks one from the dates in `lumio.promo_page`.
  Evergreen is the server default, so no JavaScript means evergreen, never a
  stale sale. The rules in stateOf() are the same as promoStateFor() in
  supabase/functions/_shared/promoPage.ts. Change one, change both.

  Sale text is written with textContent only, never as HTML.

  PREVIEW: `lumio.promo_page_preview` is used only when the URL carries
  `lumio_preview=<token>` matching it and it has not expired, the same rule as
  the lumio-storefront-sale snippet.
*/
(function () {
  if (window.LumioPromoPage) return;
  var timer = null;
  // Titles of the collections in the sale grid on show, set by swapGrid.
  var shownNow = [];
  var api = (window.LumioPromoPage = { run: run, state: 'evergreen', sale: null, page: null, preview: false });

  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function readJson(attr) {
    var el = document.querySelector('[' + attr + ']');
    if (!el) return null;
    try { return JSON.parse(el.getAttribute(attr)); } catch (e) { return null; }
  }
  function ms(iso) {
    var n = Date.parse(iso || '');
    return isNaN(n) ? null : n;
  }
  function current() {
    var pv = readJson('data-lumio-promo-preview');
    var m = /[?&]lumio_preview=([^&#]+)/.exec(window.location.search);
    var given = '';
    try { given = m ? decodeURIComponent(m[1]) : ''; } catch (e) { given = ''; }
    api.preview = !!(pv && pv.page && given && given === pv.token && Date.now() < (ms(pv.expires_at) || 0));
    if (api.preview) return pv.page;
    return readJson('data-lumio-promo-data');
  }
  function stateOf(sale, now, graceHours) {
    if (!sale) return 'evergreen';
    var grace = graceHours * 3600000;
    var starts = ms(sale.starts_at);
    var ends = ms(sale.ends_at);
    var shows = sale.extended && sale.extended.enabled ? ms(sale.extended.shows_ending_at) : null;
    if (sale.status === 'ended') {
      var ended = ms(sale.ended_at) || ends;
      return ended !== null && now - ended <= grace ? 'ended' : 'evergreen';
    }
    if (starts !== null && now < starts) return 'scheduled';
    if (ends !== null && now > ends) return now - ends <= grace ? 'ended' : 'evergreen';
    if (shows !== null && now > shows) return 'extended';
    return 'live';
  }
  function unlocked(sale) {
    try { return !!localStorage.getItem('lumio_unlocked_' + sale.key); } catch (e) { return false; }
  }
  function dateText(t, tz) {
    if (t === null) return '';
    try {
      return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', timeZone: tz || 'UTC' }).format(new Date(t));
    } catch (e) {
      return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(new Date(t));
    }
  }
  function moneyText(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
  function countdownTarget(sale, state) {
    if (!sale) return null;
    var shows = sale.extended && sale.extended.enabled ? ms(sale.extended.shows_ending_at) : null;
    if (state === 'scheduled') return ms(sale.starts_at);
    if (state === 'live') return shows !== null ? shows : ms(sale.ends_at);
    if (state === 'extended') return ms(sale.ends_at);
    return null;
  }
  // "20%" or "$10" for a plain percent or amount offer with no ladder; '' for
  // every other offer, where a short figure would misstate it.
  function offerValue(sale, currency) {
    var o = sale && sale.offer;
    if (!o || (o.tiers && o.tiers.length) || !(o.value > 0)) return '';
    // An offer with a minimum is not a plain number: "{offer} {scope}." would read
    // "20% off orders of $100+ the entire store.", and a "20% OFF" sticker would
    // promise every card the discount. It stands as the heading by itself instead.
    if (o.minimum_type && o.minimum_type !== 'none' && o.minimum_value > 0) return '';
    if (o.type === 'percentage') return o.value + '%';
    if (o.type === 'amount') return moneyText(o.value * 100, currency).replace(/\.00$/, '');
    return '';
  }
  // The percent a card may take off its own price: a plain percent off the
  // whole store with no minimum. Anything narrower cannot be judged per card.
  // An offer limited to collections prices a card only inside the sale grid, and
  // only when Console says the offer covers every collection that grid shows.
  function cardPercent(sale, state, inSaleGrid) {
    if (state !== 'live' && state !== 'extended') return 0;
    var o = sale && sale.offer;
    if (!o || o.type !== 'percentage' || (o.tiers && o.tiers.length)) return 0;
    if (o.minimum_type && o.minimum_type !== 'none') return 0;
    var limited = (o.collections && o.collections.length) || (sale.scope && sale.scope.all === false);
    if (limited && !(inSaleGrid && sale.products && sale.products.covered === true)) return 0;
    if (o.purchase_type === 'subscription') return 0;
    return o.value > 0 && o.value < 100 ? o.value : 0;
  }
  // "A", "A and B", "A, B and C". Past three the names stop reading as a phrase.
  function joinNames(list, many) {
    var n = (list || []).filter(function (x) { return !!x; });
    if (n.length < 2) return n[0] || '';
    if (n.length === 2) return n[0] + ' and ' + n[1];
    return n.length === 3 ? n[0] + ', ' + n[1] + ' and ' + n[2] : many;
  }
  // What the sale covers. Page data written before `scope` existed still says it
  // through the offer: no collections there means the whole store.
  function scopeOf(sale) {
    if (sale && sale.scope) return sale.scope;
    var o = sale && sale.offer;
    return o ? { all: !(o.collections && o.collections.length), collections: o.collections || [] } : { all: false, collections: [] };
  }
  // The {scope} token, in the brand's own words (two settings on the hero).
  function scopeText(sale, allText, someText) {
    var sc = scopeOf(sale);
    if (sc.all) return allText;
    var names = joinNames(sc.collections, 'selected collections');
    // A function, so a "$" in a collection title is text and not a replace pattern.
    return names ? someText.replace('{collections}', function () { return names; }) : '';
  }
  function tokensFor(page, sale, state) {
    if (!sale) return { offer: '', offer_value: '', sale_name: '', code: '', start_date: '', end_date: '', scope: '', collections: '', shown: '' };
    var tz = page.timezone;
    var shows = sale.extended && sale.extended.enabled ? ms(sale.extended.shows_ending_at) : null;
    var end = state === 'live' && shows !== null ? shows : state === 'ended' ? (ms(sale.ended_at) || ms(sale.ends_at)) : ms(sale.ends_at);
    // While the code is hidden, a how-to step that names it is replaced whole
    // (codeLine), because "Enter code {code} at checkout" cannot read well with
    // any stand-in word. In the theme editor text, {code} falls back to "the code".
    var code = sale.code;
    var codeLine = '';
    if (state === 'scheduled') {
      code = 'the code';
      codeLine = 'The code appears here when the sale starts.';
    } else if (sale.gated && !unlocked(sale)) {
      code = 'the code';
      codeLine = 'Unlock the code above, then enter it at checkout.';
    }
    var hero = document.querySelector('[data-lumio-scope-all]');
    return {
      offer: sale.offer_text || sale.name,
      offer_value: offerValue(sale, page.currency),
      sale_name: sale.name,
      code: code,
      codeLine: codeLine,
      start_date: dateText(ms(sale.starts_at), tz),
      end_date: dateText(end, tz),
      scope: scopeText(sale, hero ? hero.getAttribute('data-lumio-scope-all') : 'Sitewide', hero ? hero.getAttribute('data-lumio-scope-some') || '' : 'On {collections}'),
      collections: joinNames(scopeOf(sale).collections, 'selected collections'),
      shown: joinNames(shownNow, 'the sale collections'),
    };
  }
  function fill(text, tokens) {
    return text.replace(/\{(offer_value|offer|sale_name|code|start_date|end_date|scope|collections|shown)\}/g, function (_m, k) { return tokens[k] || ''; });
  }
  function applyTokens(tokens) {
    var roots = document.querySelectorAll('[data-lumio-promo-tokens]');
    for (var r = 0; r < roots.length; r++) {
      var walker = document.createTreeWalker(roots[r], NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        if (node.__lumioPromoText === undefined) {
          if (node.data.indexOf('{') < 0) continue;
          node.__lumioPromoText = node.data;
        }
        node.data = fill(node.__lumioPromoText, tokens);
      }
    }
  }
  // Custom hero words for this sale, typed in Console (sale > Promotions page >
  // Custom text). "scheduled" is before the start; "live" is while it runs and
  // when extended. They replace the theme's heading and text in that state's
  // block and are written as text, never HTML. {offer} keeps the theme's <em>.
  function heroText(sale, state, tokens) {
    var key = state === 'scheduled' ? 'scheduled' : state === 'live' || state === 'extended' ? 'live' : '';
    var want = key && sale && sale.text && sale.text[key] ? sale.text[key] : null;
    var made = document.querySelectorAll('[data-lumio-custom]');
    for (var i = 0; i < made.length; i++) made[i].parentNode.removeChild(made[i]);
    // Give back what an earlier run hid. Gated nodes are left to their gate:
    // the state loop and gate() already set them for this run, and kindGate()
    // runs after this.
    var hid = document.querySelectorAll('[data-lumio-custom-hid]');
    for (var u = 0; u < hid.length; u++) {
      hid[u].removeAttribute('data-lumio-custom-hid');
      var gated = false;
      for (var a = 0; a < hid[u].attributes.length; a++) {
        var an = hid[u].attributes[a].name;
        if (an.indexOf('data-lumio-promo-if') === 0 || an === 'data-lumio-promo-show' || an === 'data-lumio-promo-kind') gated = true;
      }
      if (!gated) hid[u].hidden = false;
    }
    if (!want || (!want.heading && !want.text)) return;
    var blocks = document.querySelectorAll('.lp-hero__state[data-lumio-promo-show="' + state + '"]');
    for (var b = 0; b < blocks.length; b++) {
      var block = blocks[b];
      if (want.heading) {
        var olds = block.querySelectorAll('.lp-hero__h, .lp-hero__offer');
        // The large-offer style keeps its look: the words go where the offer line
        // was, and its sale-name line and the line under the offer stay.
        var stack = !!block.querySelector('.lp-hero__offer, .lp-hero__name, .lp-hero__tail');
        var h = document.createElement(stack ? 'p' : 'h1');
        h.className = stack ? 'lp-hero__offer' : 'lp-hero__h';
        h.setAttribute('data-lumio-custom', '');
        var parts = want.heading.split('{offer}');
        for (var p = 0; p < parts.length; p++) {
          if (parts[p]) h.appendChild(document.createTextNode(fill(parts[p], tokens)));
          if (p < parts.length - 1) {
            var em = document.createElement('em');
            em.textContent = tokens.offer;
            h.appendChild(em);
          }
        }
        var eyebrow = block.querySelector('.lp-hero__eyebrow, .lp-hero__name');
        if (olds.length) olds[0].parentNode.insertBefore(h, olds[0]);
        else if (eyebrow) eyebrow.parentNode.insertBefore(h, eyebrow.nextSibling);
        else block.insertBefore(h, block.firstChild);
        for (var o = 0; o < olds.length; o++) { olds[o].hidden = true; olds[o].setAttribute('data-lumio-custom-hid', ''); }
      }
      if (want.text) {
        var oldText = block.querySelectorAll('.lp-hero__text');
        var t = document.createElement('p');
        t.className = 'lp-hero__text';
        t.setAttribute('data-lumio-custom', '');
        var lines = fill(want.text, tokens).split('\n');
        for (var l = 0; l < lines.length; l++) {
          if (l) t.appendChild(document.createElement('br'));
          t.appendChild(document.createTextNode(lines[l]));
        }
        if (oldText.length) oldText[0].parentNode.insertBefore(t, oldText[0]);
        else block.appendChild(t);
        for (var x = 0; x < oldText.length; x++) { oldText[x].hidden = true; oldText[x].setAttribute('data-lumio-custom-hid', ''); }
      }
    }
  }
  // Per-sale lines typed in Console (Custom text): the bottom band's line, the
  // FAQ start answer, the products line and the final band's button words. Each replaces every version of its
  // line ([data-lumio-slot]) in that state; blank leaves the theme's line for
  // the sale's kind. Runs after heroText, whose cleanup removes what the last
  // run made here too. Written as text, never HTML.
  function slotText(sale, state, tokens) {
    var key = state === 'scheduled' ? 'scheduled' : state === 'live' || state === 'extended' ? 'live' : '';
    var want = key && sale && sale.text && sale.text[key] ? sale.text[key] : null;
    if (!want) return;
    var slots = ['band', 'faq', 'products', 'cta'];
    for (var s = 0; s < slots.length; s++) {
      var words = want[slots[s]];
      if (!words) continue;
      var els = all('[data-lumio-slot="' + slots[s] + '"]');
      var parents = [];
      for (var e = 0; e < els.length; e++) if (parents.indexOf(els[e].parentNode) < 0) parents.push(els[e].parentNode);
      for (var p = 0; p < parents.length; p++) {
        var mine = els.filter(function (x) { return x.parentNode === parents[p]; });
        var made = document.createElement(mine[0].nodeName.toLowerCase());
        if (mine[0].className) made.className = mine[0].className;
        made.setAttribute('data-lumio-custom', '');
        var lines = fill(words, tokens).split('\n');
        for (var l = 0; l < lines.length; l++) {
          if (l) made.appendChild(document.createElement('br'));
          made.appendChild(document.createTextNode(lines[l]));
        }
        parents[p].insertBefore(made, mine[0]);
        for (var m = 0; m < mine.length; m++) { mine[m].hidden = true; mine[m].setAttribute('data-lumio-custom-hid', ''); }
      }
    }
  }

  // Lines written for one kind of sale ([data-lumio-promo-kind] = "code",
  // "auto", "perk", or a list). Console sends the kind (promoKindOf); page data
  // from before kinds existed has none and reads as code, the lines the page
  // always showed. A line a custom one replaced stays hidden.
  function kindGate(sale) {
    var kind = sale && (sale.kind === 'auto' || sale.kind === 'perk') ? sale.kind : 'code';
    var els = document.querySelectorAll('[data-lumio-promo-kind]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].hasAttribute('data-lumio-custom-hid')) continue;
      var ok = (' ' + els[i].getAttribute('data-lumio-promo-kind') + ' ').indexOf(' ' + kind + ' ') >= 0;
      if (els[i].hasAttribute('data-lumio-promo-show')) { if (!ok) els[i].hidden = true; }
      else els[i].hidden = !ok;
    }
  }

  // A picture link in widths for srcset, when Shopify's image CDN can resize it
  // (cdn.shopify.com, or the store's own /cdn/shop/). Anything else: one size.
  function imageSet(url) {
    if (!/^https:\/\/(cdn\.shopify\.com\/|[^/]+\/cdn\/shop\/)/i.test(url)) return '';
    var base = url.replace(/([?&])width=\d+(&|$)/, '$1').replace(/[?&]$/, '');
    var join = base.indexOf('?') < 0 ? '?' : '&';
    return [400, 700, 1000, 1400].map(function (w) { return base + join + 'width=' + w + ' ' + w + 'w'; }).join(', ');
  }
  function setImg(img, src, srcset, sizes) {
    if (src) img.setAttribute('src', src);
    if (srcset) img.setAttribute('srcset', srcset); else img.removeAttribute('srcset');
    if (sizes) img.setAttribute('sizes', sizes); else img.removeAttribute('sizes');
  }
  // Custom hero picture for this sale (sale > Promotions page > Custom image),
  // before the start and while it runs. The theme's picture is kept on the
  // element the first time one is swapped in, and given back on the next run.
  // A sale with none never touches the image. `mobile` goes in a <source> for
  // phones (the page's 749px break); with only a desktop one, phones show it.
  function heroImage(sale, state) {
    var key = state === 'scheduled' ? 'scheduled' : state === 'live' || state === 'extended' ? 'live' : '';
    var want = key && sale && sale.image && sale.image[key] ? sale.image[key] : null;
    if (want && !want.desktop && !want.mobile) want = null;
    var imgs = all('.lp-hero__media img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.hasAttribute('data-lumio-custom-img')) {
        var pic = img.parentNode;
        if (pic && pic.hasAttribute && pic.hasAttribute('data-lumio-custom-pic')) {
          pic.parentNode.insertBefore(img, pic);
          pic.parentNode.removeChild(pic);
        }
        var themeSrc = img.getAttribute('data-lumio-theme-src');
        if (!themeSrc) img.removeAttribute('src');
        setImg(img, themeSrc, img.getAttribute('data-lumio-theme-srcset'), img.getAttribute('data-lumio-theme-sizes'));
        img.removeAttribute('data-lumio-custom-img');
      }
      if (!want) continue;
      if (!img.hasAttribute('data-lumio-theme-src')) {
        img.setAttribute('data-lumio-theme-src', img.getAttribute('src') || '');
        img.setAttribute('data-lumio-theme-srcset', img.getAttribute('srcset') || '');
        img.setAttribute('data-lumio-theme-sizes', img.getAttribute('sizes') || '');
      }
      img.setAttribute('data-lumio-custom-img', '');
      if (want.desktop) setImg(img, want.desktop, imageSet(want.desktop), img.getAttribute('data-lumio-theme-sizes'));
      if (want.mobile) {
        var p = document.createElement('picture');
        p.setAttribute('data-lumio-custom-pic', '');
        // The picture box takes no part in layout: the img sizes as it did.
        p.style.display = 'contents';
        var source = document.createElement('source');
        source.setAttribute('media', '(max-width: 749px)');
        source.setAttribute('srcset', imageSet(want.mobile) || want.mobile);
        source.setAttribute('sizes', '100vw');
        img.parentNode.insertBefore(p, img);
        p.appendChild(source);
        p.appendChild(img);
      }
    }
  }

  function listInto(el, items, tokens) {
    while (el.firstChild) el.removeChild(el.firstChild);
    for (var i = 0; i < items.length; i++) {
      var li = document.createElement('li');
      li.textContent = tokens.codeLine && items[i].indexOf('{code}') >= 0 ? tokens.codeLine : fill(items[i], tokens);
      el.appendChild(li);
    }
    el.hidden = items.length === 0;
  }
  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function tick(target) {
    var left = Math.max(0, target - Date.now());
    var parts = { d: Math.floor(left / 86400000), h: Math.floor(left / 3600000) % 24, m: Math.floor(left / 60000) % 60, s: Math.floor(left / 1000) % 60 };
    var els = document.querySelectorAll('[data-lumio-promo-countdown] [data-unit]');
    for (var i = 0; i < els.length; i++) els[i].textContent = pad(parts[els[i].getAttribute('data-unit')]);
    var short = parts.d + 'd ' + pad(parts.h) + 'h ' + pad(parts.m) + 'm';
    var lefts = document.querySelectorAll('[data-lumio-promo-left]');
    for (var j = 0; j < lefts.length; j++) lefts[j].textContent = short;
    if (left === 0 && Date.now() > target) run();
  }

  // ---- prices on cards, product rows and the quick add panel ----
  function paintPrices(root) {
    var sale = api.sale;
    var pctPage = cardPercent(sale, api.state, false);
    var pctGrid = cardPercent(sale, api.state, true);
    var locked = !!(sale && sale.gated && !unlocked(sale));
    var cards = all('[data-lumio-card]', root || document);
    // A product row is repainted on its own after an option is picked.
    if (root && root.matches && root.matches('[data-lumio-card]')) cards.unshift(root);
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var section = card.closest('[data-lumio-products]');
      var price = card.querySelector('[data-lumio-price]');
      if (!price) continue;
      var cents = Number(price.getAttribute('data-cents'));
      // The quick add panel carries the mark of the card that opened it.
      var pct = card.closest('[data-lumio-salegrid]') || card.hasAttribute('data-lumio-in-sale') ? pctGrid : pctPage;
      var on = pct > 0 && cents > 0 && !(section && section.hasAttribute('data-no-sale-price')) && !card.hasAttribute('data-sold-out');
      var currency = section ? section.getAttribute('data-currency') : 'USD';
      var saleCents = on ? Math.round(cents * (100 - pct) / 100) : cents;
      var saleEl = price.querySelector('[data-lumio-sale]');
      var note = card.querySelector('[data-lumio-sale-note]');
      var badge = card.querySelector('[data-lumio-badge]');
      price.classList.toggle('is-sale', on);
      if (saleEl) { saleEl.hidden = !on; saleEl.textContent = on ? moneyText(saleCents, currency) : ''; }
      if (note) {
        // The quick add panel prints the note on its own line, as a sentence.
        var full = note.getAttribute('data-lumio-sale-note') === 'full';
        note.hidden = !on;
        if (!on) note.textContent = '';
        else if (!sale.code) note.textContent = full ? 'This price is applied at checkout.' : 'Applied at checkout';
        else if (locked) note.textContent = full ? 'Enter the sale code at checkout for this price.' : 'with the sale code at checkout';
        else note.textContent = full ? 'Enter code ' + sale.code + ' at checkout for this price.' : 'with ' + sale.code + ' at checkout';
      }
      if (badge) {
        var base = badge.getAttribute('data-base') || '';
        badge.textContent = on ? 'Save ' + pct + '%' : base;
        badge.hidden = !(on || base);
      }
      var buy = card.querySelector('[data-lumio-buy-price]');
      if (buy) buy.textContent = moneyText(saleCents, currency);
    }
  }

  // The sale's own collections in place of the brand's normal tabs, from "starts
  // soon" to the end of the sale. A grid is shown only if it was rendered for
  // this very sale, from the same source (live data or the preview), for the
  // same collections, and holds a card. Otherwise the normal tabs stay: a page
  // Shopify cached before the sale was saved has no such grid yet.
  function swapGrid(sale, state) {
    shownNow = [];
    var cols = sale && sale.products && sale.products.collections ? sale.products.collections : [];
    var on = cols.length > 0 && (state === 'scheduled' || state === 'live' || state === 'extended');
    var want = cols.map(function (c) { return c.handle; }).join(',');
    var src = api.preview ? 'preview' : 'live';
    all('[data-lumio-products]').forEach(function (sec) {
      var match = null;
      all('[data-lumio-salegrid]', sec).forEach(function (g) {
        var mine = on && !match && g.getAttribute('data-lumio-salegrid') === sale.key && g.getAttribute('data-src') === src &&
          g.getAttribute('data-want') === want && !!g.querySelector('[data-lumio-card]');
        if (mine) match = g;
        g.hidden = !mine;
      });
      // {shown} names what this grid really holds: a collection with nothing
      // published to the storefront was left out when the page was drawn.
      if (match) shownNow = all('[data-lumio-tabpanel]', match).map(function (x) { return x.getAttribute('data-title') || ''; });
      all('[data-lumio-basegrid]', sec).forEach(function (b) { b.hidden = !!match; });
      all('[data-lumio-salegrid-on]', sec).forEach(function (h) { h.hidden = !match; });
      all('[data-lumio-salegrid-off]', sec).forEach(function (h) { h.hidden = !!match; });
    });
  }

  // An element shown only under a condition. One that also carries
  // data-lumio-promo-show was just set by its state, so the condition may only
  // hide it; one without is owned by the condition alone.
  function gate(selector, ok) {
    var els = document.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) {
      if (els[i].hasAttribute('data-lumio-promo-show')) { if (!ok) els[i].hidden = true; }
      else els[i].hidden = !ok;
    }
  }

  function run() {
    var page = current();
    var sale = page && page.sale ? page.sale : null;
    var graceEl = document.querySelector('[data-lumio-promo-grace]');
    var grace = graceEl ? parseInt(graceEl.getAttribute('data-lumio-promo-grace'), 10) : 48;
    if (isNaN(grace)) grace = 48;
    var state = stateOf(sale, Date.now(), grace);
    api.state = state;
    api.sale = sale;
    api.page = page;
    var root = document.documentElement;
    root.setAttribute('data-lumio-promo-state', state);

    var shown = document.querySelectorAll('[data-lumio-promo-show]');
    for (var i = 0; i < shown.length; i++) {
      var list = ' ' + shown[i].getAttribute('data-lumio-promo-show') + ' ';
      shown[i].hidden = list.indexOf(' ' + state + ' ') < 0;
    }

    swapGrid(sale, state);

    var tokens = tokensFor(page || {}, sale, state);
    applyTokens(tokens);

    // A long offer ("Buy 2, get 1 free") gets a smaller headline size in CSS.
    var hasOffer = !!(sale && sale.offer_text);
    root.setAttribute('data-lumio-promo-offer', !hasOffer ? 'none' : tokens.offer.length > 9 ? 'long' : 'short');
    gate('[data-lumio-promo-if-offer]', hasOffer);
    gate('[data-lumio-promo-if-no-offer]', !hasOffer);
    gate('[data-lumio-promo-if-simple]', !!tokens.offer_value);
    gate('[data-lumio-promo-if-other-offer]', hasOffer && !tokens.offer_value);
    heroText(sale, state, tokens);
    slotText(sale, state, tokens);

    var hasCode = !!(sale && sale.code);
    var locked = !!(sale && sale.gated && !unlocked(sale));
    gate('[data-lumio-promo-if-code]', hasCode);
    gate('[data-lumio-promo-if-no-code]', !hasCode);
    kindGate(sale);
    heroImage(sale, state);
    var chips = document.querySelectorAll('[data-lumio-promo-code]');
    for (var k = 0; k < chips.length; k++) {
      chips[k].textContent = hasCode ? sale.code : '';
      chips[k].setAttribute('data-code', hasCode ? sale.code : '');
      chips[k].hidden = locked;
      if (locked) chips[k].classList.add('lumio-gated');
    }
    var gates = document.querySelectorAll('[data-lumio-promo-gate]');
    for (var g = 0; g < gates.length; g++) {
      gates[g].textContent = (sale && sale.gate_text) || 'Unlock the sale';
      gates[g].hidden = !locked;
    }

    var steps = document.querySelectorAll('[data-lumio-promo-steps]');
    var notes = document.querySelectorAll('[data-lumio-promo-notes]');
    var how = sale && sale.how_to ? sale.how_to : { steps: [], notes: [] };
    for (var s = 0; s < steps.length; s++) listInto(steps[s], how.steps || [], tokens);
    for (var n = 0; n < notes.length; n++) listInto(notes[n], how.notes || [], tokens);
    var terms = document.querySelectorAll('[data-lumio-promo-terms]');
    for (var t = 0; t < terms.length; t++) {
      var text = sale && sale.promo_terms ? sale.promo_terms : '';
      var body = terms[t].querySelector('[data-lumio-promo-terms-text]');
      if (body) body.textContent = text;
      if (!text) terms[t].hidden = true;
    }

    paintPrices();

    // The first question a shopper can see starts open.
    var faqs = document.querySelectorAll('[data-lumio-faq][data-open-first]');
    for (var f = 0; f < faqs.length; f++) {
      var items = all('details', faqs[f]).filter(function (x) { return !x.hidden; });
      if (items.length && !items.some(function (x) { return x.open; })) items[0].open = true;
    }

    if (timer) { clearInterval(timer); timer = null; }
    var target = countdownTarget(sale, state);
    if (target !== null) {
      tick(target);
      timer = setInterval(function () { tick(target); }, 1000);
    }
    stick();
    try { document.dispatchEvent(new CustomEvent('lumio:promo:state', { detail: { state: state } })); } catch (err) {}
  }

  // ---- phone bar: on while a sale runs and the hero button is off screen ----
  var seen = { cta: true, close: false };
  var io = null;
  function watch(el, key) {
    if (!el || el.__lumioWatched || !io) return;
    el.__lumioWatched = key;
    io.observe(el);
  }
  function stick() {
    var bar = document.querySelector('[data-lumio-stick]');
    if (!bar) return;
    if (!io && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) seen[entries[i].target.__lumioWatched] = entries[i].isIntersecting;
        stick();
      });
    }
    // Looked up on every run: the first run happens while the page is still
    // parsing, before the closing section exists.
    watch(document.querySelector('[data-lumio-cta]'), 'cta');
    watch(document.querySelector('[data-lumio-close]'), 'close');
    bar.classList.toggle('is-on', !bar.hidden && !!io && !seen.cta && !seen.close);
  }

  // ---- best sellers: cart ----
  function openedDawn(el) {
    if (el.classList.contains('active')) return true;
    return !!(el.notification && el.notification.classList && el.notification.classList.contains('active'));
  }
  // Tells the theme about the new item. Failures fall back to /cart.
  // Order: Kaching Cart, a Dawn or Shrine cart-drawer / cart-notification,
  // Palo Alto's theme:cart:refresh event, then the cart page.
  function refresh(res, variantId, dawn) {
    var kaching = window.kachingCartApi;
    if (kaching && typeof kaching.refreshCart === 'function') {
      // new Promise also turns a synchronous throw into the /cart fallback.
      new Promise(function (ok) { ok(kaching.refreshCart()); }).then(function () {
        if (typeof kaching.openCart === 'function') kaching.openCart();
      }, function () { window.location.href = '/cart'; });
      return;
    }
    if (dawn && res.sections) {
      try {
        // Publish first, redraw second, the order the theme's own product form
        // uses. On Shrine (an older Dawn) the cart items element answers the
        // event by loading the cart page's markup into itself. Redrawing after
        // the event replaces that element, so its answer lands on nothing.
        // The other order left the cart page's table inside the drawer.
        if (typeof window.publish === 'function') {
          try { window.publish('cart-update', { source: 'lumio-promo', productVariantId: variantId, cartData: res }); } catch (e) {}
        }
        dawn.classList.remove('is-empty');
        dawn.renderContents(res);
      } catch (e) {
        window.location.href = '/cart';
        return;
      }
      // The cart page is the fallback only when the theme's drawer or notice
      // never opened. One the shopper closed within two seconds counts as
      // opened: without this, clicking anywhere on the page right after an add
      // closed AET's notice and then sent the shopper to /cart.
      var shown = false;
      var watch = setInterval(function () { if (openedDawn(dawn)) { shown = true; clearInterval(watch); } }, 100);
      setTimeout(function () { if (!shown && !openedDawn(dawn) && typeof dawn.open === 'function') dawn.open(); }, 400);
      setTimeout(function () { clearInterval(watch); if (!shown && !openedDawn(dawn)) window.location.href = '/cart'; }, 2000);
      return;
    }
    if (document.querySelector('[data-cart-drawer]')) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      return;
    }
    window.location.href = '/cart';
  }
  function add(variantId, button, errorEl, qty) {
    var dawn = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (dawn && (typeof dawn.getSectionsToRender !== 'function' || typeof dawn.renderContents !== 'function')) dawn = null;
    var body = { id: Number(variantId), quantity: Math.max(1, parseInt(qty, 10) || 1) };
    if (dawn && !window.kachingCartApi) {
      try {
        body.sections = dawn.getSectionsToRender().map(function (x) { return x.id; });
        body.sections_url = window.location.pathname;
      } catch (e) { dawn = null; }
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.textContent = '';
    var done = function () { button.disabled = false; button.removeAttribute('aria-busy'); };
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        done();
        if (!res.ok) {
          var msg = (res.data && (res.data.description || res.data.message)) || 'Could not add that. Please try the product page.';
          if (errorEl) errorEl.textContent = msg; else window.alert(msg);
          return false;
        }
        refresh(res.data, variantId, dawn);
        return true;
      })
      .catch(function () {
        done();
        if (errorEl) errorEl.textContent = 'Something went wrong. Please try the product page.';
        return false;
      });
  }

  // ---- best sellers: quick add panel ----
  // Product data comes from /products/<handle>.js the first time a product is
  // opened and is kept for the life of the page.
  var products = {};
  function clear(el) { while (el.firstChild) el.removeChild(el.firstChild); }
  function part(panel, name) { return panel.querySelector('[data-lumio-qa-' + name + ']'); }
  function calm() { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
  // Product JSON gives protocol-relative image URLs.
  function imgUrl(src, w) {
    if (!src) return '';
    var u = src.indexOf('//') === 0 ? 'https:' + src : src;
    return u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=' + w;
  }
  function slide(src, alt, lazy) {
    var img = document.createElement('img');
    img.alt = alt;
    if (lazy) img.loading = 'lazy';
    img.src = src;
    return img;
  }
  function slideIndex(track) {
    var w = track.clientWidth;
    return w ? Math.round(track.scrollLeft / w) : 0;
  }
  function countSlides(panel) {
    var track = part(panel, 'track');
    var total = track.children.length;
    part(panel, 'count').textContent = total > 1 ? (slideIndex(track) + 1) + ' / ' + total : '';
    all('[data-lumio-qa-nav]', panel).forEach(function (b) { b.hidden = total < 2; });
  }
  function slideTo(panel, i, smooth) {
    var track = part(panel, 'track');
    var total = track.children.length;
    if (!total) return;
    var idx = Math.max(0, Math.min(total - 1, i));
    track.scrollTo({ left: idx * track.clientWidth, behavior: smooth && !calm() ? 'smooth' : 'auto' });
    countSlides(panel);
  }

  // A product description is HTML typed in the Shopify admin. It is parsed in
  // a detached document, where nothing runs or loads, and rebuilt here from
  // text and the plain tags below. Nothing from it is inserted as markup.
  var KEEP = { P: 'p', DIV: 'div', UL: 'ul', OL: 'ol', LI: 'li', BR: 'br', STRONG: 'strong', B: 'strong', EM: 'em', I: 'em', TR: 'p', H1: 'p', H2: 'p', H3: 'p', H4: 'p', H5: 'p', H6: 'p' };
  var DROP = /^(SCRIPT|STYLE|IFRAME|IMG|PICTURE|VIDEO|AUDIO|SVG|OBJECT|EMBED|LINK|META|NOSCRIPT|TEMPLATE|FORM|INPUT|BUTTON|SELECT|TEXTAREA)$/;
  function copySafe(from, to, depth) {
    if (depth > 20) return;
    for (var n = from.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3) { to.appendChild(document.createTextNode(n.data)); continue; }
      if (n.nodeType !== 1) continue;
      var tag = n.nodeName.toUpperCase();
      if (DROP.test(tag)) continue;
      var as = KEEP[tag];
      if (!as) {
        copySafe(n, to, depth + 1);
        if (tag === 'TD' || tag === 'TH') to.appendChild(document.createTextNode(' '));
        continue;
      }
      var el = document.createElement(as);
      if (/^H[1-6]$/.test(tag)) el.className = 'lp-qa__h';
      copySafe(n, el, depth + 1);
      // A paragraph that only held a picture is left out.
      if (as === 'br' || el.textContent.replace(/\s+/g, '')) to.appendChild(el);
    }
  }
  function descInto(el, html) {
    clear(el);
    var doc = null;
    try { doc = new DOMParser().parseFromString(String(html || ''), 'text/html'); } catch (e) { doc = null; }
    if (doc && doc.body) copySafe(doc.body, el, 0);
    if (!el.textContent.replace(/\s+/g, '')) { clear(el); el.textContent = 'No description available.'; }
  }

  function matchVariant(st) {
    return st.product.variants.filter(function (x) {
      return st.chosen.every(function (val, k) { return x.options[k] === val; });
    })[0] || null;
  }
  // In stock with the other options as they are now.
  function inStockHere(st, i, val) {
    return st.product.variants.some(function (x) {
      return x.available && x.options[i] === val && st.chosen.every(function (v, k) { return k === i || x.options[k] === v; });
    });
  }
  function inStockAnywhere(st, i, val) {
    return st.product.variants.some(function (x) { return x.available && x.options[i] === val; });
  }
  // The in-stock variant with this value that keeps the most of the other choices.
  function nearest(st, i, val) {
    var best = null;
    var bestScore = -1;
    st.product.variants.forEach(function (x) {
      if (!x.available || x.options[i] !== val) return;
      var score = st.chosen.filter(function (v, k) { return k !== i && x.options[k] === v; }).length;
      if (score > bestScore) { best = x; bestScore = score; }
    });
    return best;
  }
  function pick(panel, i, val) {
    var st = panel.__lumio;
    if (!st || !st.product) return;
    st.chosen[i] = val;
    // A choice that is sold out with the other options moves them to a
    // combination that is in stock, when one exists.
    var hit = matchVariant(st);
    if (!hit || !hit.available) {
      var fix = nearest(st, i, val);
      if (fix) st.chosen = fix.options.slice();
    }
    choose(panel);
  }
  // Paints the panel for the options chosen now.
  function choose(panel) {
    var st = panel.__lumio;
    var section = panel.closest('[data-lumio-products]');
    var currency = section.getAttribute('data-currency');
    var submit = part(panel, 'submit');
    var price = panel.querySelector('[data-lumio-price]');
    var now = panel.querySelector('[data-lumio-now]');
    var compare = panel.querySelector('[data-lumio-compare]');
    var badge = panel.querySelector('[data-lumio-badge]');
    st.groups.forEach(function (g, i) {
      if (g.select) {
        g.select.value = st.chosen[i];
        all('option', g.select).forEach(function (o) { o.disabled = !inStockAnywhere(st, i, o.value); });
        return;
      }
      g.pills.forEach(function (b, k) {
        b.setAttribute('aria-pressed', String(g.values[k] === st.chosen[i]));
        b.disabled = !inStockAnywhere(st, i, g.values[k]);
        b.classList.toggle('is-out', !b.disabled && !inStockHere(st, i, g.values[k]));
      });
    });
    var v = matchVariant(st);
    st.variant = v;
    submit.removeAttribute('data-variant-id');
    if (!v) {
      now.textContent = '';
      compare.hidden = true;
      price.setAttribute('data-cents', '0');
      if (badge) badge.setAttribute('data-base', '');
      submit.disabled = true;
      submit.textContent = section.getAttribute('data-label-sold-out');
      paintPrices(panel);
      return;
    }
    var was = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : 0;
    now.textContent = moneyText(v.price, currency);
    compare.hidden = !was;
    compare.textContent = was ? moneyText(was, currency) : '';
    if (badge) badge.setAttribute('data-base', was ? 'Save ' + moneyText(was - v.price, currency).replace(/\.00$/, '') : '');
    price.setAttribute('data-cents', String(v.price));
    submit.setAttribute('data-variant-id', v.id);
    submit.disabled = !v.available;
    submit.textContent = section.getAttribute(v.available ? 'data-label-add' : 'data-label-sold-out');
    // The gallery jumps to the picture of the chosen variant.
    if (v.featured_image && typeof v.featured_image.position === 'number') slideTo(panel, v.featured_image.position - 1, false);
    paintPrices(panel);
  }
  function build(panel, p) {
    var st = panel.__lumio;
    var variants = p.variants || [];
    if (!variants.length) throw new Error('no variants');
    st.product = p;
    st.chosen = (variants.filter(function (x) { return x.available; })[0] || variants[0]).options.slice();
    part(panel, 'title').textContent = p.title || '';

    // The first picture is already in place from the card. The rest follow it.
    var track = part(panel, 'track');
    var images = p.images || [];
    var have = track.children.length;
    for (var n = have; n < images.length; n++) track.appendChild(slide(imgUrl(images[n], 800), (p.title || '') + ' image ' + (n + 1), n > 0));
    countSlides(panel);

    var desc = part(panel, 'desc');
    if (desc) descInto(desc, p.description);

    var box = part(panel, 'options');
    clear(box);
    st.groups = [];
    var single = variants.length === 1 && /default/i.test(variants[0].title || '');
    if (!single) (p.options || []).forEach(function (opt, i) {
      var values = [];
      variants.forEach(function (x) { if (values.indexOf(x.options[i]) < 0) values.push(x.options[i]); });
      var name = typeof opt === 'string' ? opt : opt.name;
      var wrap = document.createElement('div');
      wrap.className = 'lp-qa__opt';
      var label = document.createElement('span');
      label.className = 'lp-qa__lab';
      label.textContent = name;
      wrap.appendChild(label);
      if (values.length <= 12) {
        var row = document.createElement('div');
        row.className = 'lp-opts';
        var pills = values.map(function (val) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'lp-opt';
          b.textContent = val;
          b.addEventListener('click', function () { pick(panel, i, val); });
          row.appendChild(b);
          return b;
        });
        wrap.appendChild(row);
        st.groups.push({ pills: pills, values: values });
      } else {
        var sel = document.createElement('select');
        sel.setAttribute('aria-label', name);
        values.forEach(function (val) {
          var o = document.createElement('option');
          o.value = val;
          o.textContent = val;
          sel.appendChild(o);
        });
        sel.addEventListener('change', function () { pick(panel, i, sel.value); });
        wrap.appendChild(sel);
        st.groups.push({ select: sel, values: values });
      }
      box.appendChild(wrap);
    });
    choose(panel);
  }
  function closePanel(panel) {
    panel.hidden = true;
    panel.__lumioTicket = (panel.__lumioTicket || 0) + 1;
    document.documentElement.style.removeProperty('overflow');
    // Keyboard focus goes back to the button that opened the panel.
    var opener = panel.__lumioOpener;
    panel.__lumioOpener = null;
    if (opener && opener.focus && document.contains(opener)) opener.focus();
  }
  // Returns false when there is nothing to open, so a link can go on to the product page.
  function openPanel(opener) {
    var card = opener.closest('[data-lumio-card]');
    var section = opener.closest('[data-lumio-products]');
    var panel = section ? section.querySelector('[data-lumio-qa-panel]') : null;
    var handle = card ? card.getAttribute('data-handle') : '';
    if (!panel || !handle) return false;
    var title = card.getAttribute('data-title') || '';
    var spec = card.getAttribute('data-spec') || '';
    var track = part(panel, 'track');
    var submit = part(panel, 'submit');
    var error = part(panel, 'error');
    panel.__lumio = { product: null, chosen: [], groups: [], variant: null };
    // A card in the sale grid is priced by the sale grid's rule, and so is its panel.
    var box = panel.querySelector('[data-lumio-card]');
    if (box && card.closest('[data-lumio-salegrid]')) box.setAttribute('data-lumio-in-sale', '');
    else if (box) box.removeAttribute('data-lumio-in-sale');

    part(panel, 'title').textContent = title;
    part(panel, 'spec').textContent = spec;
    part(panel, 'qty').value = 1;
    part(panel, 'full').href = card.getAttribute('data-url') || '#';
    error.textContent = '';
    clear(part(panel, 'options'));
    clear(track);
    // The first picture shows at once from the card's own image, which the
    // browser already has, and is swapped for the larger file when that loads.
    var big = card.getAttribute('data-image');
    var thumb = card.querySelector('.lp-card__media img');
    var have = thumb && (thumb.currentSrc || thumb.src);
    if (big || have) {
      var lead = slide(have || big, title, false);
      track.appendChild(lead);
      if (big && have) {
        var hi = new Image();
        hi.onload = function () { if (lead.parentNode === track) lead.src = big; };
        hi.src = big;
      }
    }
    var desc = part(panel, 'desc');
    if (desc) clear(desc);
    all('details', panel).forEach(function (d) { d.open = false; });

    // Fit facts come from the product's metafields, printed on the card.
    var fit = part(panel, 'fit');
    if (fit) {
      var any = false;
      all('[data-lumio-qa-fact]', fit).forEach(function (x) {
        var val = card.getAttribute('data-f' + x.getAttribute('data-lumio-qa-fact')) || '';
        x.hidden = !val;
        x.lastElementChild.textContent = val;
        if (val) any = true;
      });
      var size = part(fit, 'size');
      var extra = (card.getAttribute('data-f4') || '').replace(/\.+$/, '');
      var line = ((spec ? size.getAttribute('data-prefix') + ' ' + spec + '. ' : '') + (extra ? extra + '.' : '')).trim();
      size.textContent = line;
      size.hidden = !line;
      fit.hidden = !any && !line;
    }

    panel.querySelector('[data-lumio-now]').textContent = '';
    panel.querySelector('[data-lumio-compare]').hidden = true;
    panel.querySelector('[data-lumio-price]').setAttribute('data-cents', '0');
    var badge = panel.querySelector('[data-lumio-badge]');
    if (badge) badge.setAttribute('data-base', '');
    var bss = panel.querySelector('[bss-b2b-product-price]');
    if (bss) bss.setAttribute('bss-b2b-product-id', card.getAttribute('data-product-id') || '');
    submit.disabled = true;
    submit.removeAttribute('data-variant-id');
    submit.textContent = section.getAttribute('data-label-add');

    panel.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    panel.__lumioOpener = opener;
    // Each open gets a number. A product response that arrives after the panel
    // was closed or reopened for another product is dropped.
    var ticket = (panel.__lumioTicket = (panel.__lumioTicket || 0) + 1);
    var scroller = panel.querySelector('.lp-qa__panel');
    if (scroller) scroller.scrollTop = 0;
    track.scrollLeft = 0;
    countSlides(panel);
    paintPrices(panel);
    if (!track.__lumioBound) {
      // The counter follows a swipe once the scroll settles.
      track.__lumioBound = true;
      var wait = null;
      track.addEventListener('scroll', function () {
        clearTimeout(wait);
        wait = setTimeout(function () { countSlides(panel); }, 90);
      }, { passive: true });
    }
    var closer = panel.querySelector('button[data-lumio-qa-close]');
    if (closer) closer.focus();

    var ready = function (p) {
      if (panel.__lumioTicket !== ticket || panel.hidden) return;
      try { build(panel, p); } catch (e) { error.textContent = 'Could not load this product. Please try the product page.'; }
    };
    if (products[handle]) { ready(products[handle]); return true; }
    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(function (p) { products[handle] = p; ready(p); })
      .catch(function () {
        if (panel.__lumioTicket !== ticket) return;
        error.textContent = 'Could not load this product. Please try the product page.';
      });
    return true;
  }

  // ---- product rows: pick an option ----
  function pickOption(btn) {
    var card = btn.closest('[data-lumio-card]');
    var section = btn.closest('[data-lumio-products]');
    var currency = section ? section.getAttribute('data-currency') : 'USD';
    var pills = all('[data-lumio-opt]', card);
    for (var i = 0; i < pills.length; i++) pills[i].setAttribute('aria-pressed', String(pills[i] === btn));
    var cents = Number(btn.getAttribute('data-cents'));
    var compareCents = Number(btn.getAttribute('data-compare')) || 0;
    var price = card.querySelector('[data-lumio-price]');
    var now = card.querySelector('[data-lumio-now]');
    var compare = card.querySelector('[data-lumio-compare]');
    var badge = card.querySelector('[data-lumio-badge]');
    var buy = card.querySelector('[data-lumio-row-add]');
    price.setAttribute('data-cents', String(cents));
    now.textContent = moneyText(cents, currency);
    compare.hidden = !(compareCents > cents);
    if (compareCents > cents) compare.textContent = moneyText(compareCents, currency);
    if (badge) badge.setAttribute('data-base', compareCents > cents ? 'Save ' + moneyText(compareCents - cents, currency).replace(/\.00$/, '') : '');
    if (buy) buy.setAttribute('data-variant-id', btn.getAttribute('data-variant-id'));
    paintPrices(card);
  }

  // ---- video: load the player only when asked ----
  // YouTube's player script, fetched on the first play click and never before.
  var ytWait = null;
  function ytApi() {
    if (window.YT && window.YT.Player) return Promise.resolve();
    if (!ytWait) {
      ytWait = new Promise(function (ok, fail) {
        var prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = function () { if (typeof prev === 'function') prev(); ok(); };
        var tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.onerror = function () { ytWait = null; fail(new Error('youtube player script')); };
        document.head.appendChild(tag);
      });
    }
    return ytWait;
  }
  // The plain embed, used when the player script cannot load.
  function ytPlain(box, id, label) {
    var frame = document.createElement('iframe');
    frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';
    frame.title = label;
    frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
    frame.allowFullscreen = true;
    box.appendChild(frame);
  }
  function play(btn) {
    var box = btn.closest('[data-lumio-video]');
    if (!box) return;
    var yt = box.getAttribute('data-youtube');
    var poster = box.querySelector('img');
    if (yt) {
      var label = btn.getAttribute('aria-label') || 'Video';
      var holder = document.createElement('div');
      box.appendChild(holder);
      var fallback = function () {
        if (!holder) return;
        if (holder.parentNode) holder.parentNode.removeChild(holder);
        holder = null;
        ytPlain(box, yt, label);
      };
      var late = setTimeout(fallback, 4000);
      ytApi().then(function () {
        clearTimeout(late);
        if (!holder) return;
        var mount = holder;
        holder = null;
        new window.YT.Player(mount, {
          width: '100%',
          height: '100%',
          videoId: yt,
          host: 'https://www.youtube-nocookie.com',
          playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
          events: {
            onReady: function (ev) {
              ev.target.playVideo();
              // Safari will not start a video with sound unless the click was
              // inside the player. Tested 2026-10-01: the player loaded and sat
              // still. If it has not started, start it muted. YouTube shows its
              // own unmute button. 1 is playing, 3 is buffering.
              setTimeout(function () {
                try {
                  var st = ev.target.getPlayerState();
                  if (st !== 1 && st !== 3) { ev.target.mute(); ev.target.playVideo(); }
                } catch (e) {}
              }, 1500);
            },
          },
        });
      }, function () { clearTimeout(late); fallback(); });
    } else {
      var v = box.querySelector('video');
      if (!v) return;
      v.controls = true;
      v.hidden = false;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    if (poster) poster.hidden = true;
    btn.hidden = true;
  }

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;

    var chip = t.closest('[data-lumio-promo-code], [data-lumio-promo-gate]');
    if (chip) {
      if (chip.hasAttribute('data-lumio-promo-gate')) {
        e.preventDefault();
        if (window.LumioSale && typeof window.LumioSale.open === 'function') window.LumioSale.open();
        return;
      }
      var code = chip.getAttribute('data-code');
      if (!code) return;
      var copied = function () {
        chip.textContent = 'Copied';
        setTimeout(function () { chip.textContent = code; }, 1500);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(copied, function () {});
      } catch (err) {}
      return;
    }

    var tab = t.closest('[data-lumio-tab]');
    if (tab) {
      // The normal tabs and each sale grid are tab groups of their own.
      var group = tab.closest('[data-lumio-tabs]') || tab.closest('[data-lumio-products]');
      var want = tab.getAttribute('data-lumio-tab');
      all('[data-lumio-tab]', group).forEach(function (x) { x.setAttribute('aria-selected', String(x === tab)); });
      all('[data-lumio-tabpanel]', group).forEach(function (x) { x.hidden = x.getAttribute('data-lumio-tabpanel') !== want; });
      return;
    }
    var opt = t.closest('[data-lumio-opt]');
    if (opt) { e.preventDefault(); pickOption(opt); return; }
    var openBtn = t.closest('[data-lumio-qa-open]');
    if (openBtn) {
      // The product picture is a real link: a modified click still opens the product page.
      if (openBtn.tagName === 'A' && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1)) return;
      if (openPanel(openBtn)) e.preventDefault();
      return;
    }
    var addBtn = t.closest('[data-lumio-row-add]');
    if (addBtn) { e.preventDefault(); add(addBtn.getAttribute('data-variant-id'), addBtn, null, 1); return; }
    var nav = t.closest('[data-lumio-qa-nav]');
    if (nav) {
      e.preventDefault();
      var navPanel = nav.closest('[data-lumio-qa-panel]');
      slideTo(navPanel, slideIndex(part(navPanel, 'track')) + Number(nav.getAttribute('data-lumio-qa-nav')), true);
      return;
    }
    var step = t.closest('[data-lumio-qa-step]');
    if (step) {
      e.preventDefault();
      var qty = part(step.closest('[data-lumio-qa-panel]'), 'qty');
      qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(step.getAttribute('data-lumio-qa-step')));
      return;
    }
    var submit = t.closest('[data-lumio-qa-submit]');
    if (submit) {
      e.preventDefault();
      var id = submit.getAttribute('data-variant-id');
      if (!id) return;
      var panel = submit.closest('[data-lumio-qa-panel]');
      // Closed only if it still shows the product that was added.
      var shown = panel.__lumioTicket;
      add(id, submit, part(panel, 'error'), part(panel, 'qty').value).then(function (ok) {
        if (ok && !panel.hidden && panel.__lumioTicket === shown) closePanel(panel);
      });
      return;
    }
    var closer = t.closest('[data-lumio-qa-close]');
    if (closer) { e.preventDefault(); closePanel(closer.closest('[data-lumio-qa-panel]')); return; }
    var playBtn = t.closest('[data-lumio-play]');
    if (playBtn) { e.preventDefault(); play(playBtn); return; }
    // Buttons that point at a section of this page scroll to it smoothly.
    var jump = t.closest('.lumio-promo a[href^="#lumio-"]');
    if (jump) {
      var dest = document.querySelector(jump.getAttribute('href'));
      if (!dest) return;
      e.preventDefault();
      dest.scrollIntoView({ behavior: calm() ? 'auto' : 'smooth', block: 'start' });
    }
  });
  // The second product image is fetched the first time a card is hovered.
  document.addEventListener('pointerover', function (e) {
    var card = e.target && e.target.closest ? e.target.closest('.lp-card') : null;
    var img = card ? card.querySelector('img[data-src]') : null;
    if (!img) return;
    if (img.getAttribute('data-srcset')) img.srcset = img.getAttribute('data-srcset');
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
  }, { passive: true });
  document.addEventListener('keydown', function (e) {
    var open = all('[data-lumio-qa-panel]').filter(function (p) { return !p.hidden; })[0];
    if (!open) return;
    if (e.key === 'Escape') { closePanel(open); return; }
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    // Arrow keys move the gallery, except while typing a quantity or in a menu.
    var tag = e.target && e.target.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    slideTo(open, slideIndex(part(open, 'track')) + (e.key === 'ArrowRight' ? 1 : -1), true);
  });
  document.addEventListener('shopify:section:load', run);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
